import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { PaginationDetails } from "src/app/models/models";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { defaultListDateRange } from "src/app/shared/utils/list-date-filter.utils";
import { MyFeedbackListItem } from "../feedback.types";

@Component({
	selector: "app-my-feedback",
	templateUrl: "./my-feedback.component.html",
	styleUrls: ["./my-feedback.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MyFeedbackComponent implements OnInit, OnDestroy {
	@ViewChild("formNameTpl", { static: true }) formNameTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("instructorTpl", { static: true }) instructorTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;

	searchQuery = "";
	selectedKind: "" | "standalone" | "tied" = "";
	columns: Columns[] = [];
	configuration: Config = { ...DefaultConfig };
	submissions: MyFeedbackListItem[] = [];
	pagination: Pagination = {
		limit: CONSTANT.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	private page = CONSTANT.PAGINATION.DEFAULT_PAGE;
	private size = CONSTANT.PAGINATION.DEFAULT_SIZE;
	private subs = new Subscription();
	private listSub?: Subscription;
	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	private filterStart = moment(this.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	private filterEnd = moment(this.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	dateFilterEnabled = false;
	private searchTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private sharedService: SharedService,
		private roleService: RoleService,
		private cdr: ChangeDetectorRef,
	) {}

	get pageTitle(): string {
		return this.roleService.hasPilotView || this.roleService.hasInstructorView ? "Feedback History" : "Feedback";
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.MY_FEEDBACK);
		this.initTableConfig();
		this.loadSubmissions();
	}

	ngOnDestroy(): void {
		this.listSub?.unsubscribe();
		this.subs.unsubscribe();
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
	}

	initTableConfig(): void {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = CONSTANT.PAGINATION.MAX_PAGINATION_COUNT;
		this.configuration.rows = this.size;
		const instructorView = this.roleService.hasInstructorView;
		this.columns = [
			{
				key: "formName",
				title: "Form Name",
				cellTemplate: this.formNameTpl,
				width: instructorView ? "45%" : "28%",
			},
			{
				key: "formTypeLabel",
				title: "Form Type",
				cellTemplate: this.formTypeTpl,
				width: instructorView ? "25%" : "18%",
			},
			...(instructorView
				? []
				: [{ key: "instructorName", title: "From", cellTemplate: this.instructorTpl, width: "18%" }]),
			{
				key: "submittedAt",
				title: "Date Submitted",
				cellTemplate: this.dateTpl,
				width: instructorView ? "18%" : "22%",
			},
			{ key: "", title: "Action", cellTemplate: this.actionTpl, width: instructorView ? "12%" : "14%" },
		];
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
			this.filterStart = moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
			this.filterEnd = moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
		} else {
			return;
		}

		this.page = 1;
		this.loadSubmissions();
	}

	loadSubmissions(): void {
		this.loaderService.setLoading(true);
		this.listSub?.unsubscribe();
		this.listSub = this.httpService
			.listMyFeedback({
				page: this.page,
				size: this.size,
				search: this.searchQuery.trim() || undefined,
				kind: this.selectedKind || undefined,
				role: this.roleService.getUserRole(),
				...(this.dateFilterEnabled ? { startDate: this.filterStart, endDate: this.filterEnd } : {}),
			})
			.subscribe({
				next: (resp) => {
					const payload = resp?.data ?? resp;
					this.submissions = payload?.data ?? [];
					const total = payload?.total ?? 0;
					const paginationDetails = this.calculatePages(total, this.size);
					this.pagination = {
						limit: this.size,
						offset: this.page,
						count: this.size * paginationDetails.totalPages,
					};
					this.loaderService.setLoading(false);
					this.cdr.detectChanges();
				},
				error: () => this.loaderService.setLoading(false),
			});
	}

	onSearch(): void {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.page = 1;
			this.loadSubmissions();
		}, 300);
	}

	onKindFilter(): void {
		this.page = 1;
		this.loadSubmissions();
	}

	onTableChanged(event: any): void {
		if (event.event !== "onPagination") return;
		this.page = event.value?.page ?? this.page;
		this.size = event.value?.limit ?? this.size;
		this.configuration.rows = this.size;
		this.loadSubmissions();
	}

	viewSubmission(submissionId: string): void {
		this.router.navigate([RouteType.MY_FEEDBACK, submissionId]);
	}

	formName(row: MyFeedbackListItem): string {
		if (!row.isTied) return row.templateName || "-";
		return row.formName || "-";
	}

	formType(row: MyFeedbackListItem): string {
		if (!row.isTied) return "Feedback";
		return row.formTypeLabel || "-";
	}

	fromLabel(row: MyFeedbackListItem): string {
		if (!row.isTied) return "Training Department";
		return row.instructorName || "-";
	}

	formatDate(value?: string): string {
		if (!value) return "-";
		return moment(value).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage) || 1;
		const lastPageSize = totalDataCount % rowsPerPage;
		return { totalPages, lastPageSize };
	}
}
